import { ChromePicker } from 'react-color';
import { MdRefresh } from 'react-icons/md';
import RedStar from '../RedStar';
import { useEffect, useRef, useState } from 'react';

type ColorInputProps<T> = {
  name: keyof T;
  label: string;
  defaultColor: string;
  isLoading: boolean;
  register: any;
  setValue: any;
  watch: any;
  error?: string;
  isEditing?: boolean;
  readOnly?: boolean;
  description?: string;
};

export const ColorInput = <T,>({
  name,
  label,
  defaultColor,
  isLoading,
  register,
  setValue,
  watch,
  error,
  isEditing,
  readOnly,
  description,
}: ColorInputProps<T>) => {
  const [show, setShow] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const currentColor = watch(name) ?? defaultColor;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setShow(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (isLoading) {
    return (
      <div>
        <label className='block text-sm font-medium text-gray-700 mb-2'>
          &nbsp;
        </label>
        <div className='flex items-center w-full space-x-2 relative animate-pulse'>
          <div className='w-full h-10 bg-gray-200 rounded-md' />
          <div className='w-1/5 h-10 flex rounded border border-gray-300 shadow-inner overflow-hidden'>
            <div className='w-1/2 bg-gray-300' />
            <div className='w-1/2 bg-gray-200' />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <label className='block text-sm font-medium text-gray-700 mb-2'>
        {label} <RedStar />
      </label>
      <div className='flex items-center w-full space-x-2 relative'>
        <input
          type='text'
          {...register(name)}
          value={currentColor}
          className={`register-input w-full border border-gray-300 rounded-md px-3 py-2 ${
            !isEditing && 'read-only-input'
          }`}
          readOnly={readOnly}
        />
        <div className='w-1/5 h-10 flex rounded border border-gray-300 shadow-inner overflow-hidden'>
          <div
            className='w-1/2 cursor-pointer'
            style={{ backgroundColor: currentColor }}
            onClick={() => !readOnly && setShow(true)}
          />
          <div
            className='w-1/2 flex justify-center items-center text-gray-500 cursor-pointer'
            onClick={() =>
              !readOnly &&
              setValue(name, defaultColor, { shouldValidate: true })
            }
          >
            <MdRefresh />
          </div>
        </div>

        {show && (
          <div className='absolute top-12 right-0 z-10' ref={ref}>
            <ChromePicker
              color={currentColor}
              onChange={(color: any) =>
                setValue(name, color.hex, { shouldValidate: true })
              }
              disableAlpha
            />
          </div>
        )}
      </div>
      {description && (
        <p className='text-[0.75rem]  text-[#878787] '>{description}</p>
      )}
      {error && (
        <p className='text-xs font-semibold text-red-600 mt-1'>{error}</p>
      )}
    </div>
  );
};
